import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Snackbar,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  PlayArrow as TestIcon,
  History as HistoryIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../hooks/useUserData';
import FacilityAssignmentTree from '../common/FacilityAssignmentTree';
import {
  getHispConnections,
  createHispConnection,
  updateHispConnection,
  deleteHispConnection,
  testHispConnection,
  getFacilitiesWithHispConnections
} from '../../services/hispConnectionService';
import {
  sendDirectMessage,
  sendClinicalDocument,
  getDirectMessageLogs,
  createAttachmentFromFile,
  DocumentType,
  DirectMessageContentType
} from '../../services/directMessagingService';
import { safeFormatDate } from '../../utils/dateUtils';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hisp-tabpanel-${index}`}
      aria-labelledby={`hisp-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Initial form state
const initialFormState = {
  active: true,
  name: '',
  description: '',
  type: 'api',
  // Outbound API Configuration (for sending messages)
  apiKey: '',
  apiSecret: '',
  endpoint: '',
  // Inbound Configuration (for receiving messages)
  inboundEnabled: true,
  inboundApiKey: '', // API key the HISP must use when calling our webhook
  // SMTP Configuration
  smtpServer: '',
  smtpPort: '587',
  smtpUsername: '',
  smtpPassword: '',
  fromEmail: '',
  // TLS Settings
  useTls: true,
  // Facility assignments
  assignedFacilities: []
};

// Get the webhook URL for inbound messages
const getInboundWebhookUrl = () => {
  // Use the hosting URL with rewrite rules (works in both dev and prod)
  return `${window.location.origin}/directMessaging/direct/receive`;
};

const HISPConnection = () => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [testingId, setTestingId] = useState(null);
  const [disabledFacilities, setDisabledFacilities] = useState([]);
  
  // Main page tabs (Connections vs Message Logs)
  const [mainTab, setMainTab] = useState(0);
  
  // Message Logs state
  const [messageLogs, setMessageLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDetailOpen, setLogDetailOpen] = useState(false);
  
  // Compose message state
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState('message'); // 'message' or 'document'
  const [sending, setSending] = useState(false);
  const [messageForm, setMessageForm] = useState({
    hispConnectionId: '',
    to: '',
    subject: '',
    body: '',
    attachments: []
  });
  const [documentForm, setDocumentForm] = useState({
    hispConnectionId: '',
    to: '',
    documentType: DocumentType.CCD,
    documentFormat: 'CCDA',
    patientName: '',
    patientId: '',
    documentContent: ''
  });

  const navigate = useNavigate();
  const { isAdmin, defaultFacility } = useUserData();

  // Fetch connections
  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getHispConnections();
      setConnections(result.connections || []);
    } catch (error) {
      console.error('Error fetching HISP connections:', error);
      setSnackbar({ open: true, message: 'Failed to load HISP connections', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Fetch facilities that already have HISP connections
  const fetchDisabledFacilities = useCallback(async (excludeConnectionId = null) => {
    try {
      const assigned = await getFacilitiesWithHispConnections(excludeConnectionId);
      const formatted = assigned.map(a => ({
        facilityId: a.facilityId,
        reason: `Already assigned to "${a.connectionName}"`
      }));
      setDisabledFacilities(formatted);
    } catch (error) {
      console.error('Error fetching assigned facilities:', error);
      setDisabledFacilities([]);
    }
  }, []);

  // Open dialog for new connection
  const handleAdd = async () => {
    setEditId(null);
    setForm(initialFormState);
    setTabValue(0);
    await fetchDisabledFacilities(null);
    setOpen(true);
  };

  // Open dialog for editing
  const handleEdit = async (connection) => {
    setEditId(connection.id);
    setForm({
      active: connection.active ?? true,
      name: connection.name || '',
      description: connection.description || '',
      type: connection.type || 'api',
      apiKey: '', // Don't pre-fill secrets
      apiSecret: '',
      endpoint: connection.endpoint || '',
      smtpServer: connection.smtpServer || '',
      smtpPort: connection.smtpPort || '587',
      smtpUsername: connection.smtpUsername || '',
      smtpPassword: '',
      fromEmail: connection.fromEmail || '',
      useTls: connection.useTls ?? true,
      assignedFacilities: connection.assignedFacilities || []
    });
    setTabValue(0);
    await fetchDisabledFacilities(connection.id); // Exclude current connection
    setOpen(true);
  };

  // Close dialog
  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm(initialFormState);
    setTabValue(0);
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!form.name.trim()) {
        setSnackbar({ open: true, message: 'Connection name is required', severity: 'error' });
        return;
      }

      // Prepare data
      const connectionData = {
        name: form.name,
        description: form.description,
        type: form.type,
        active: form.active,
        assignedFacilities: form.assignedFacilities,
        useTls: form.useTls
      };

      // Add type-specific fields
      if (form.type === 'api') {
        connectionData.endpoint = form.endpoint;
        if (form.apiKey) connectionData.apiKey = form.apiKey;
        if (form.apiSecret) connectionData.apiSecret = form.apiSecret;
      } else {
        connectionData.smtpServer = form.smtpServer;
        connectionData.smtpPort = form.smtpPort;
        connectionData.smtpUsername = form.smtpUsername;
        connectionData.fromEmail = form.fromEmail;
        if (form.smtpPassword) connectionData.smtpPassword = form.smtpPassword;
      }

      if (editId) {
        await updateHispConnection(editId, connectionData);
        setSnackbar({ open: true, message: 'HISP connection updated successfully', severity: 'success' });
      } else {
        await createHispConnection(connectionData);
        setSnackbar({ open: true, message: 'HISP connection created successfully', severity: 'success' });
      }

      handleClose();
      fetchConnections();
    } catch (error) {
      console.error('Error saving HISP connection:', error);
      setSnackbar({ open: true, message: 'Failed to save HISP connection', severity: 'error' });
    }
  };

  // Delete connection
  const handleDelete = async () => {
    try {
      await deleteHispConnection(deleteConfirm.id, true);
      setSnackbar({ open: true, message: 'HISP connection deleted successfully', severity: 'success' });
      setDeleteConfirm({ open: false, id: null, name: '' });
      fetchConnections();
    } catch (error) {
      console.error('Error deleting HISP connection:', error);
      setSnackbar({ open: true, message: 'Failed to delete HISP connection', severity: 'error' });
    }
  };

  // Test connection
  const handleTest = async (connectionId) => {
    try {
      setTestingId(connectionId);
      const result = await testHispConnection(connectionId);
      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: `Connection test successful (${result.responseTime}ms)`, 
          severity: 'success' 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: result.message || 'Connection test failed', 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('Error testing HISP connection:', error);
      setSnackbar({ open: true, message: 'Connection test failed', severity: 'error' });
    } finally {
      setTestingId(null);
    }
  };

  // Render connection type badge
  const renderTypeBadge = (type) => {
    return (
      <Chip 
        label={type === 'api' ? 'API' : 'SMTP'} 
        size="small" 
        color={type === 'api' ? 'primary' : 'secondary'}
        variant="outlined"
      />
    );
  };
  
  // ===== MESSAGE LOGS FUNCTIONS =====
  
  // Fetch message logs
  const fetchMessageLogs = useCallback(async () => {
    if (!defaultFacility?.id) return;
    try {
      setLogsLoading(true);
      const result = await getDirectMessageLogs({ facilityId: defaultFacility.id, limit: 100 });
      setMessageLogs(result.logs || []);
    } catch (error) {
      console.error('Error fetching message logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [defaultFacility?.id]);
  
  // Load logs when switching to logs tab
  useEffect(() => {
    if (mainTab === 1) {
      fetchMessageLogs();
    }
  }, [mainTab, fetchMessageLogs]);
  
  // Get status chip for message logs
  const getStatusChip = (status) => {
    const statusConfig = {
      sent: { color: 'info', icon: <ScheduleIcon fontSize="small" /> },
      delivered: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
      failed: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
      pending: { color: 'warning', icon: <ScheduleIcon fontSize="small" /> }
    };
    const config = statusConfig[status] || { color: 'default' };
    return (
      <Chip
        size="small"
        label={status}
        color={config.color}
        icon={config.icon}
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };
  
  // Open compose dialog
  const handleOpenCompose = (mode = 'message') => {
    setComposeMode(mode);
    setMessageForm({
      hispConnectionId: connections.length === 1 ? connections[0].id : '',
      to: '',
      subject: '',
      body: '',
      attachments: []
    });
    setDocumentForm({
      hispConnectionId: connections.length === 1 ? connections[0].id : '',
      to: '',
      documentType: DocumentType.CCD,
      documentFormat: 'CCDA',
      patientName: '',
      patientId: '',
      documentContent: ''
    });
    setComposeOpen(true);
  };
  
  // Handle file attachment
  const handleFileAttach = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    try {
      const newAttachments = await Promise.all(
        Array.from(files).map(file => createAttachmentFromFile(file))
      );
      setMessageForm(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newAttachments]
      }));
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to attach file', severity: 'error' });
    }
    event.target.value = '';
  };
  
  // Handle document file selection
  const handleDocumentFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const attachment = await createAttachmentFromFile(file);
      setDocumentForm(prev => ({
        ...prev,
        documentContent: attachment.content
      }));
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to read document file', severity: 'error' });
    }
    event.target.value = '';
  };
  
  // Send message
  const handleSendMessage = async () => {
    if (!messageForm.to || !messageForm.subject) {
      setSnackbar({ open: true, message: 'Recipient and subject are required', severity: 'error' });
      return;
    }
    if (!messageForm.hispConnectionId) {
      setSnackbar({ open: true, message: 'Please select a HISP connection', severity: 'error' });
      return;
    }
    setSending(true);
    try {
      const result = await sendDirectMessage({
        ...messageForm,
        facilityId: defaultFacility?.id
      });
      if (result.success) {
        setSnackbar({ open: true, message: `Message sent successfully. ID: ${result.messageId}`, severity: 'success' });
        setComposeOpen(false);
        fetchMessageLogs();
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to send message', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.error || err.message || 'Failed to send message', severity: 'error' });
    } finally {
      setSending(false);
    }
  };
  
  // Send document
  const handleSendDocument = async () => {
    if (!documentForm.to || !documentForm.documentType || !documentForm.documentContent) {
      setSnackbar({ open: true, message: 'Recipient, document type, and document file are required', severity: 'error' });
      return;
    }
    if (!documentForm.hispConnectionId) {
      setSnackbar({ open: true, message: 'Please select a HISP connection', severity: 'error' });
      return;
    }
    setSending(true);
    try {
      const result = await sendClinicalDocument({
        ...documentForm,
        facilityId: defaultFacility?.id
      });
      if (result.success) {
        setSnackbar({ open: true, message: `Document sent successfully. ID: ${result.messageId}`, severity: 'success' });
        setComposeOpen(false);
        fetchMessageLogs();
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to send document', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.error || err.message || 'Failed to send document', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to manage HISP connections. Admin access required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        borderRadius: '16px', 
        mb: 3,
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        py: 1.5,
        px: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                HISP & Direct Messaging
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Manage connections and send secure Direct protocol messages
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {mainTab === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)', 
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' }
                }}
              >
                New Connection
              </Button>
            )}
            {mainTab === 1 && (
              <>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => handleOpenCompose('message')}
                  disabled={connections.length === 0}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)', 
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' }
                  }}
                >
                  Send Message
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => handleOpenCompose('document')}
                  disabled={connections.length === 0}
                  sx={{ 
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }
                  }}
                >
                  Send Document
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
      
      {/* Main Tabs */}
      <Paper sx={{ borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', mb: 2 }}>
        <Tabs 
          value={mainTab} 
          onChange={(e, v) => setMainTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Connections" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Message Logs" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Connections Tab */}
      {mainTab === 0 && (
      <Paper sx={{ borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : connections.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary" gutterBottom>
              No HISP connections configured yet
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 2 }}>
              Create First Connection
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Endpoint / Server</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Facilities</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connections.map((connection) => (
                  <TableRow key={connection.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {connection.name}
                      </Typography>
                      {connection.description && (
                        <Typography variant="caption" color="text.secondary">
                          {connection.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{renderTypeBadge(connection.type)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {connection.type === 'api' 
                          ? connection.endpoint || '-'
                          : connection.smtpServer || '-'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${connection.assignedFacilities?.length || 0} facilities`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={connection.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={connection.active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Test Connection">
                        <IconButton 
                          size="small" 
                          onClick={() => handleTest(connection.id)}
                          disabled={testingId === connection.id}
                        >
                          {testingId === connection.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <TestIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(connection)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => setDeleteConfirm({ 
                            open: true, 
                            id: connection.id, 
                            name: connection.name 
                          })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      )}
      
      {/* Message Logs Tab */}
      {mainTab === 1 && (
      <Paper sx={{ borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
        {connections.length === 0 ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            No HISP connections configured. Please add a connection first before sending messages.
          </Alert>
        ) : logsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : messageLogs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary" gutterBottom>
              No message history yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Send your first Direct message to see it here
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<SendIcon />} 
              onClick={() => handleOpenCompose('message')}
            >
              Send Message
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Direction</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>To/From</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Protocol</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messageLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      {safeFormatDate(log.sentAt?._seconds ? new Date(log.sentAt._seconds * 1000) : log.sentAt, 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={log.direction}
                        color={log.direction === 'outbound' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.direction === 'outbound' ? log.to : log.from}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.subject}
                    </TableCell>
                    <TableCell>
                      {log.documentType && (
                        <Chip size="small" label={log.documentType} variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(log.status)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={log.protocol} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => { setSelectedLog(log); setLogDetailOpen(true); }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          <Button startIcon={<RefreshIcon />} onClick={fetchMessageLogs} disabled={logsLoading}>
            Refresh
          </Button>
        </Box>
      </Paper>
      )}

      {/* Edit/Create Connection Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">
              {editId ? 'Edit HISP Connection' : 'New HISP Connection'}
            </Typography>
          </Box>
        </DialogTitle>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Basic Info" icon={<SettingsIcon />} iconPosition="start" />
            <Tab label="Connection Settings" />
            <Tab label="Facilities" icon={<BusinessIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <DialogContent>
          {/* Tab 0: Basic Info */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={form.active} 
                      onChange={handleChange} 
                      name="active" 
                      color="success" 
                    />
                  }
                  label="Connection Active"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  label="Connection Name" 
                  name="name" 
                  value={form.name} 
                  onChange={handleChange} 
                  fullWidth 
                  required 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Connection Type</InputLabel>
                  <Select 
                    label="Connection Type" 
                    name="type" 
                    value={form.type} 
                    onChange={handleChange}
                  >
                    <MenuItem value="api">API Integration</MenuItem>
                    <MenuItem value="smtp">SMTP Server</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  label="Description" 
                  name="description" 
                  value={form.description} 
                  onChange={handleChange} 
                  fullWidth 
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 1: Connection Settings */}
          <TabPanel value={tabValue} index={1}>
            {form.type === 'api' ? (
              <Grid container spacing={2}>
                {/* Outbound Settings */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                    Outbound Settings (Sending Messages)
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Configure API credentials provided by your HISP for sending Direct messages
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    label="Outbound API Endpoint" 
                    name="endpoint" 
                    value={form.endpoint} 
                    onChange={handleChange} 
                    fullWidth 
                    placeholder="https://hisp-provider.com/api/v1/send"
                    helperText="The HISP endpoint for sending messages"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    label="API Key" 
                    name="apiKey" 
                    value={form.apiKey} 
                    onChange={handleChange} 
                    fullWidth 
                    helperText={editId ? 'Leave blank to keep existing' : 'Provided by your HISP'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    label="API Secret" 
                    name="apiSecret" 
                    value={form.apiSecret} 
                    onChange={handleChange} 
                    fullWidth 
                    type="password"
                    helperText={editId ? 'Leave blank to keep existing' : 'Provided by your HISP'}
                  />
                </Grid>

                {/* Divider */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                {/* Inbound Settings */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                    Inbound Settings (Receiving Messages)
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Give these settings to your HISP so they can send incoming Direct messages to your system
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={form.inboundEnabled} 
                        onChange={handleChange} 
                        name="inboundEnabled" 
                        color="success"
                      />
                    }
                    label="Enable Inbound Messages"
                  />
                </Grid>
                {form.inboundEnabled && (
                  <>
                    <Grid item xs={12}>
                      <TextField 
                        label="Webhook URL (Give to HISP)" 
                        value={getInboundWebhookUrl()}
                        fullWidth 
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Copy URL">
                                <IconButton
                                  onClick={() => {
                                    navigator.clipboard.writeText(getInboundWebhookUrl());
                                    setSnackbar({ open: true, message: 'Webhook URL copied!', severity: 'success' });
                                  }}
                                  size="small"
                                >
                                  <SettingsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          )
                        }}
                        helperText="Provide this URL to your HISP for incoming message delivery"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="Inbound API Key" 
                        name="inboundApiKey" 
                        value={form.inboundApiKey} 
                        onChange={handleChange} 
                        fullWidth 
                        helperText="Create a key for the HISP to include in X-API-Key header"
                        placeholder="Generate or enter a secure API key"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button 
                        variant="outlined" 
                        onClick={() => {
                          const key = 'hisp_' + Array.from(crypto.getRandomValues(new Uint8Array(24)))
                            .map(b => b.toString(16).padStart(2, '0')).join('');
                          setForm(prev => ({ ...prev, inboundApiKey: key }));
                        }}
                        sx={{ mt: 1 }}
                      >
                        Generate API Key
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="warning" icon={false} sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Instructions for your HISP:
                        </Typography>
                        <Typography variant="body2" component="div">
                          <ol style={{ margin: 0, paddingLeft: 20 }}>
                            <li>Configure webhook URL: <code>{getInboundWebhookUrl()}</code></li>
                            <li>Include header: <code>X-API-Key: {form.inboundApiKey || '[your-api-key]'}</code></li>
                            <li>Include header: <code>X-HISP-Connection-Id: {editId || '[connection-id]'}</code></li>
                            <li>POST JSON body with: from, to, subject, body, attachments, metadata</li>
                          </ol>
                        </Typography>
                      </Alert>
                    </Grid>
                  </>
                )}
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Configure SMTP-based Direct messaging settings
                  </Alert>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField 
                    label="SMTP Server" 
                    name="smtpServer" 
                    value={form.smtpServer} 
                    onChange={handleChange} 
                    fullWidth 
                    placeholder="smtp.direct-provider.com"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField 
                    label="Port" 
                    name="smtpPort" 
                    value={form.smtpPort} 
                    onChange={handleChange} 
                    fullWidth 
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    label="Username" 
                    name="smtpUsername" 
                    value={form.smtpUsername} 
                    onChange={handleChange} 
                    fullWidth 
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    label="Password" 
                    name="smtpPassword" 
                    value={form.smtpPassword} 
                    onChange={handleChange} 
                    fullWidth 
                    type="password"
                    helperText={editId ? 'Leave blank to keep existing' : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    label="From Email (Direct Address)" 
                    name="fromEmail" 
                    value={form.fromEmail} 
                    onChange={handleChange} 
                    fullWidth 
                    placeholder="provider@direct.facility.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={form.useTls} 
                        onChange={handleChange} 
                        name="useTls" 
                      />
                    }
                    label="Use TLS Encryption"
                  />
                </Grid>

                {/* Divider */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                {/* Inbound Settings for SMTP */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                    Inbound Settings (Receiving Messages)
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    For SMTP connections, your HISP should forward incoming messages to your webhook endpoint
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={form.inboundEnabled} 
                        onChange={handleChange} 
                        name="inboundEnabled" 
                        color="success"
                      />
                    }
                    label="Enable Inbound Messages"
                  />
                </Grid>
                {form.inboundEnabled && (
                  <>
                    <Grid item xs={12}>
                      <TextField 
                        label="Webhook URL (Give to HISP)" 
                        value={getInboundWebhookUrl()}
                        fullWidth 
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Copy URL">
                                <IconButton
                                  onClick={() => {
                                    navigator.clipboard.writeText(getInboundWebhookUrl());
                                    setSnackbar({ open: true, message: 'Webhook URL copied!', severity: 'success' });
                                  }}
                                  size="small"
                                >
                                  <SettingsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          )
                        }}
                        helperText="Provide this URL to your HISP for incoming message delivery"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        label="Inbound API Key" 
                        name="inboundApiKey" 
                        value={form.inboundApiKey} 
                        onChange={handleChange} 
                        fullWidth 
                        helperText="Create a key for the HISP to include in X-API-Key header"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button 
                        variant="outlined" 
                        onClick={() => {
                          const key = 'hisp_' + Array.from(crypto.getRandomValues(new Uint8Array(24)))
                            .map(b => b.toString(16).padStart(2, '0')).join('');
                          setForm(prev => ({ ...prev, inboundApiKey: key }));
                        }}
                        sx={{ mt: 1 }}
                      >
                        Generate API Key
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </TabPanel>

          {/* Tab 2: Facilities */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which facilities can use this HISP connection for health information exchange.
              <br />
              <Typography component="span" variant="caption" color="warning.main">
                Note: Each facility can only have one active HISP connection.
              </Typography>
            </Typography>
            <FacilityAssignmentTree
              selectedFacilities={form.assignedFacilities}
              onChange={(facilityIds) => setForm(prev => ({ ...prev, assignedFacilities: facilityIds }))}
              showSummary={true}
              maxHeight={350}
              disabledFacilities={disabledFacilities}
            />
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editId ? 'Update' : 'Create'} Connection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null, name: '' })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the HISP connection <strong>"{deleteConfirm.name}"</strong>? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, id: null, name: '' })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compose Message Dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {composeMode === 'message' ? <EmailIcon color="primary" /> : <CloudUploadIcon color="primary" />}
            <Typography variant="h6">
              {composeMode === 'message' ? 'Compose Direct Message' : 'Send Clinical Document'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {composeMode === 'message' ? (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>HISP Connection</InputLabel>
                  <Select
                    value={messageForm.hispConnectionId}
                    label="HISP Connection"
                    onChange={(e) => setMessageForm(prev => ({ ...prev, hispConnectionId: e.target.value }))}
                  >
                    {connections.filter(c => c.active).map((conn) => (
                      <MenuItem key={conn.id} value={conn.id}>
                        {conn.name} ({conn.type.toUpperCase()})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="To (Direct Address)"
                  fullWidth
                  size="small"
                  value={messageForm.to}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="recipient@provider.direct.example.com"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Subject"
                  fullWidth
                  size="small"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, subject: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Message Body"
                  fullWidth
                  multiline
                  rows={6}
                  value={messageForm.body}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, body: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button component="label" startIcon={<AttachFileIcon />} variant="outlined" size="small">
                    Attach Files
                    <input type="file" hidden multiple onChange={handleFileAttach} />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {messageForm.attachments.length} file(s) attached
                  </Typography>
                </Box>
                {messageForm.attachments.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {messageForm.attachments.map((att, index) => (
                      <Chip
                        key={index}
                        label={att.filename}
                        size="small"
                        onDelete={() => setMessageForm(prev => ({
                          ...prev,
                          attachments: prev.attachments.filter((_, i) => i !== index)
                        }))}
                      />
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>HISP Connection</InputLabel>
                  <Select
                    value={documentForm.hispConnectionId}
                    label="HISP Connection"
                    onChange={(e) => setDocumentForm(prev => ({ ...prev, hispConnectionId: e.target.value }))}
                  >
                    {connections.filter(c => c.active).map((conn) => (
                      <MenuItem key={conn.id} value={conn.id}>
                        {conn.name} ({conn.type.toUpperCase()})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="To (Direct Address)"
                  fullWidth
                  size="small"
                  value={documentForm.to}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="recipient@provider.direct.example.com"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={documentForm.documentType}
                    label="Document Type"
                    onChange={(e) => setDocumentForm(prev => ({ ...prev, documentType: e.target.value }))}
                  >
                    {Object.entries(DocumentType).map(([key, value]) => (
                      <MenuItem key={key} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Document Format</InputLabel>
                  <Select
                    value={documentForm.documentFormat}
                    label="Document Format"
                    onChange={(e) => setDocumentForm(prev => ({ ...prev, documentFormat: e.target.value }))}
                  >
                    <MenuItem value="CCDA">C-CDA (XML)</MenuItem>
                    <MenuItem value="PDF">PDF</MenuItem>
                    <MenuItem value="HL7">HL7</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Patient Name"
                  fullWidth
                  size="small"
                  value={documentForm.patientName}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, patientName: e.target.value }))}
                  helperText="Used in subject line"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Patient ID"
                  fullWidth
                  size="small"
                  value={documentForm.patientId}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, patientId: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  component="label"
                  variant="outlined"
                  fullWidth
                  startIcon={<AttachFileIcon />}
                  sx={{ py: 2 }}
                >
                  {documentForm.documentContent ? 'Document Selected ✓' : 'Select Document File'}
                  <input type="file" hidden accept=".xml,.pdf,.hl7,.cda" onChange={handleDocumentFile} />
                </Button>
                {!documentForm.documentContent && (
                  <Typography variant="caption" color="text.secondary">
                    Required - Select the clinical document to send
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setComposeOpen(false)} disabled={sending}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={composeMode === 'message' ? handleSendMessage : handleSendDocument}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Log Detail Dialog */}
      <Dialog open={logDetailOpen} onClose={() => setLogDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Message Details</DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Message ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedLog.messageId}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box>{getStatusChip(selectedLog.status)}</Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Direction</Typography>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedLog.direction}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Protocol</Typography>
                <Typography variant="body2">{selectedLog.protocol}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">From</Typography>
                <Typography variant="body2">{selectedLog.from}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">To</Typography>
                <Typography variant="body2">{selectedLog.to}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Subject</Typography>
                <Typography variant="body2">{selectedLog.subject}</Typography>
              </Grid>
              {selectedLog.documentType && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Document Type</Typography>
                  <Typography variant="body2">{selectedLog.documentType}</Typography>
                </Grid>
              )}
              {selectedLog.patientId && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Patient ID</Typography>
                  <Typography variant="body2">{selectedLog.patientId}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Sent At</Typography>
                <Typography variant="body2">
                  {safeFormatDate(selectedLog.sentAt?._seconds ? new Date(selectedLog.sentAt._seconds * 1000) : selectedLog.sentAt, 'MMM dd, yyyy HH:mm:ss')}
                </Typography>
              </Grid>
              {selectedLog.error && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {selectedLog.error}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HISPConnection;
